import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Layers } from "lucide-react";

export default function AdminSystems() {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", name: "", short: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/systems").then((r) => setSystems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name) return toast.error("Code and name are required.");
    setBusy(true);
    try {
      await api.post("/admin/systems", form);
      toast.success(`${form.code.toUpperCase()} added.`);
      setForm({ code: "", name: "", short: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add system.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 className="font-heading font-black text-3xl tracking-tight">Systems</h1>
      <p className="text-muted-foreground mt-2">Your 3-letter libraries under CBS. Add new ones as you grow.</p>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
          ) : (
            <div className="bg-white border border-border divide-y divide-border">
              {systems.map((s) => (
                <div key={s.code} data-testid={`admin-system-${s.code}`} className="flex items-center gap-4 p-4">
                  <div className="w-11 h-11 bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="font-heading font-black text-primary text-sm">{s.code}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.short} · {s.count} forms</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={add} className="bg-white border border-border p-6 h-fit space-y-4" data-testid="add-system-form">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" />
            <h2 className="font-heading font-bold">Add a system</h2>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Code (3-4 letters)</label>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={5} placeholder="CFLT" data-testid="system-code"
              className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Full name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Centennial Fleet & Logistics" data-testid="system-name"
              className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Short label</label>
            <input value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })} placeholder="Fleet" data-testid="system-short"
              className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <button type="submit" disabled={busy} data-testid="add-system-submit"
            className="w-full bg-primary text-white py-3 font-semibold btn-lift disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add system
          </button>
        </form>
      </div>
    </div>
  );
}
